import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ScreenShell } from './ScreenShared';
import { API_URL, getTokenSubject, getWebSocketBaseUrl } from '../demoMode';
import { apiGetJson, extractArray } from '../apiClient';
import { sortConversationsByLatest, totalUnreadCount, unreadConversations } from '../featureModels';
import TierBadge from './TierBadge';

export function ChatsScreen() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('messages');

  useEffect(() => {
    let active = true;

    const loadChats = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (active) {
          setLoadError('Please log in again to load chats.');
          setLoading(false);
        }
        return;
      }

      try {
        const result = await apiGetJson('/v1/chats', { token });

        if (!result.ok) {
          const attemptNotes = (result.attempts || [])
            .map((attempt) => `${attempt.url} -> ${attempt.status || 'network error'}`)
            .join(' | ');
          throw new Error(`Could not load chats (HTTP ${result.status || 0}). Attempts: ${attemptNotes}`);
        }

        if (active) {
          setConversations(extractArray(result.payload));
          setLoadError('');
        }
      } catch (err) {
        if (active) {
          setConversations([]);
          setLoadError(err instanceof Error ? err.message : 'Could not load chats.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadChats();

    return () => {
      active = false;
    };
  }, []);

  return (
    <ScreenShell>
      <div className="ic-tab-row" role="tablist" aria-label="Chats tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'messages'}
          className={`ic-tab-btn ${tab === 'messages' ? 'active' : ''}`}
          onClick={() => setTab('messages')}
        >
          Messages
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'activity'}
          className={`ic-tab-btn ${tab === 'activity' ? 'active' : ''}`}
          onClick={() => setTab('activity')}
        >
          Activity
          {totalUnreadCount(conversations) > 0 ? <span className="ic-tab-badge">{totalUnreadCount(conversations)}</span> : null}
        </button>
      </div>

      {tab === 'messages' ? (
        <div className="ic-chat-list">
          {loading ? <p className="figma-sub">Loading chats...</p> : null}
          {!loading && loadError ? <p className="figma-sub">{loadError}</p> : null}
          {!loading && !loadError && conversations.length === 0 ? (
            <div className="figma-list-card">
              <h3>No conversations yet</h3>
              <p>Start from Lounge and message someone from their profile.</p>
            </div>
          ) : null}
          {sortConversationsByLatest(conversations).map((conversation) => (
            <Link key={conversation.username} to={`/chats/${conversation.username}`} className="ic-chat-row">
              <div className="ic-chat-avatar" aria-hidden="true">
                {(conversation.name || conversation.username || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="ic-chat-main">
                <div className="ic-chat-title-row">
                  <p className="ic-chat-name">{conversation.name || conversation.username}</p>
                  <TierBadge tier={conversation.tier_id} />
                </div>
                <p className="ic-chat-preview">
                  {conversation.latest_message_from === conversation.username ? '' : 'You: '}
                  {conversation.latest_message || 'Start the conversation'}
                </p>
              </div>
              <div className="ic-chat-right">
                {conversation.unread_count > 0 ? <span className="ic-chat-unread">{conversation.unread_count}</span> : null}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="ic-activity-list">
          {unreadConversations(conversations).length === 0 ? (
            <div className="figma-list-card">
              <h3>No new activity</h3>
              <p>Unread messages and invite updates will show here.</p>
            </div>
          ) : (
            unreadConversations(conversations).map((conversation) => (
              <Link key={`activity-${conversation.username}`} to={`/chats/${conversation.username}`} className="ic-activity-row">
                <p>
                  <strong>{conversation.name || conversation.username}</strong> sent {conversation.unread_count} unread message
                  {conversation.unread_count > 1 ? 's' : ''}.
                </p>
                <span>Open</span>
              </Link>
            ))
          )}
        </div>
      )}
    </ScreenShell>
  );
}

export function ChatThreadScreen() {
  const { id } = useParams();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState('');
  const usernameRef = useRef('');
  const socketRef = useRef(null);
  const currentUsernameRef = useRef('');

  useEffect(() => {
    usernameRef.current = id || '';
  }, [id]);

  const appendOutgoingMessage = (messageText) => {
    const currentUsername = currentUsernameRef.current;
    const timestamp = new Date().toISOString();
    setMessages((current) => [
      ...current,
      {
        id: `outgoing-${timestamp}`,
        sender_username: currentUsername,
        recipient_username: usernameRef.current,
        message: messageText,
        created_at: timestamp,
        read_at: null,
      },
    ]);
  };

  const sendMessageViaRest = async (token, messageText) => {
    const response = await fetch(`${API_URL}/v1/chats/${encodeURIComponent(usernameRef.current)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message: messageText }),
    });

    if (!response.ok) {
      throw new Error(`Could not send message (HTTP ${response.status}).`);
    }

    appendOutgoingMessage(messageText);
  };

  useEffect(() => {
    let active = true;

    const loadThread = async () => {
      const token = localStorage.getItem('token');
      if (!token || !id) {
        if (active) {
          setLoadError('Missing chat target.');
          setLoading(false);
        }
        return;
      }

      try {
        const result = await apiGetJson(`/v1/chats/${encodeURIComponent(id)}`, { token });

        if (!result.ok) {
          const attemptNotes = (result.attempts || [])
            .map((attempt) => `${attempt.url} -> ${attempt.status || 'network error'}`)
            .join(' | ');
          throw new Error(`Could not load thread (HTTP ${result.status || 0}). Attempts: ${attemptNotes}`);
        }

        if (active) {
          setMessages(extractArray(result.payload));
          setLoadError('');
        }
      } catch (err) {
        if (active) {
          setMessages([]);
          setLoadError(err instanceof Error ? err.message : 'Could not load chat thread.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadThread();

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const currentUsername = getTokenSubject(token || '');
    if (!token || !currentUsername) {
      return undefined;
    }

    currentUsernameRef.current = currentUsername;

    const wsBase = getWebSocketBaseUrl(API_URL);
    const wsUrl = `${wsBase}/ws/${encodeURIComponent(currentUsername)}?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;
    let heartbeatTimer = null;

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
      heartbeatTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
        }
      }, 15000);
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload?.type !== 'chat' || payload?.from !== usernameRef.current) {
          return;
        }

        setMessages((current) => [
          ...current,
          {
            id: `incoming-${payload.timestamp}-${payload.from}`,
            sender_username: payload.from,
            recipient_username: currentUsername,
            message: payload.message,
            created_at: payload.timestamp,
            read_at: null,
          },
        ]);
      } catch (_err) {
        // Ignore malformed websocket messages.
      }
    };

    return () => {
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
      }
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      socket.close();
    };
  }, []);

  const handleSend = async (event) => {
    event.preventDefault();

    const trimmed = draft.trim();
    const token = localStorage.getItem('token');
    if (!trimmed || !token || !id || sending) {
      return;
    }

    setSending(true);

    try {
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'chat',
            target: id,
            message: trimmed,
            timestamp: Date.now(),
          })
        );
        appendOutgoingMessage(trimmed);
      } else {
        await sendMessageViaRest(token, trimmed);
      }
      setDraft('');
      setLoadError('');
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not send message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <ScreenShell
      showMainNav={true}
      titleBar={
        <div className="ic-thread-topbar">
          <Link className="figma-toplink" to="/chats">Back</Link>
          <span className="ic-thread-title">@{id}</span>
          <Link className="figma-toplink" to={`/call/${id}`}>Call</Link>
        </div>
      }
    >
      <div className="ic-thread-wrap">
        {loading ? <p className="figma-sub">Loading messages...</p> : null}
        {!loading && loadError ? <p className="figma-sub">{loadError}</p> : null}
        {!loading && !loadError && messages.length === 0 ? <p className="figma-sub">Say hello.</p> : null}
        {messages.map((message) => (
          <div key={message.id} className={`ic-thread-bubble ${message.sender_username !== id ? 'me' : ''}`}>
            <p>{message.message}</p>
            <small>{new Date(message.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
          </div>
        ))}
      </div>
      <form className="ic-thread-input-row" onSubmit={handleSend}>
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={`Message @${id}`}
          maxLength={4000}
        />
        <button type="submit" disabled={sending || !draft.trim()}>
          {sending ? '...' : 'Send'}
        </button>
      </form>
    </ScreenShell>
  );
}
